import React, { useState, useEffect } from 'react';
import { StudentDetail, FeeStatementRow } from '../types';
import FeeStatementTable from '../components/FeeStatementTable';
import { studentService } from '../services/studentService';

interface StudentDetailPageProps {
  student: StudentDetail;
  onBack: () => void;
  onProceedPayment: () => void;
}

type FeeType = 'tuition' | 'transportation' | 'both';

const StudentDetailPage: React.FC<StudentDetailPageProps> = ({
  student,
  onBack,
  onProceedPayment,
}) => {
  const [selectedFeeType, setSelectedFeeType] = useState<FeeType>('both');
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [tuitionStatement, setTuitionStatement] = useState<FeeStatementRow[]>([]);
  const [transportationStatement, setTransportationStatement] = useState<FeeStatementRow[]>([]);
  const [loadingStatements, setLoadingStatements] = useState<boolean>(false);
  const [selectedTuitionRowIndex, setSelectedTuitionRowIndex] = useState<number | null>(null);
  const [selectedTransportationRowIndex, setSelectedTransportationRowIndex] = useState<number | null>(null);
  const [exportingTuition, setExportingTuition] = useState<boolean>(false);
  const [exportingTransportation, setExportingTransportation] = useState<boolean>(false);
  
  const feeDetails = student.feeDetails;
  const apiFeeDetails = student.fee_details;
  const dueDate = new Date(feeDetails.dueDate);
  const today = new Date();
  const isOverdue = today > dueDate;
  const daysUntilDue = Math.ceil(
    (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Fetch fee statements
  useEffect(() => {
    const fetchFeeStatements = async () => {
      if (!student.sub_acct_code) return;
      
      setLoadingStatements(true);
      try {
        const [tuitionData, transportationData] = await Promise.all([
          studentService.getTuitionFeeStatement(student.sub_acct_code),
          studentService.getTransportationFeeStatement(student.sub_acct_code),
        ]);

        setTuitionStatement(tuitionData || []);
        setTransportationStatement(transportationData || []);
      } catch (error) {
        console.error('Error fetching fee statements:', error);
      } finally {
        setLoadingStatements(false);
      }
    };

    fetchFeeStatements();
  }, [student.sub_acct_code]);

  // Handle row selection from tuition statement
  const handleTuitionRowSelect = (index: number | null) => {
    setSelectedTuitionRowIndex(index);
    setSelectedFeeType('both'); // Reset radio selection (will be overridden by payment calculation)
  };

  // Handle row selection from transportation statement
  const handleTransportationRowSelect = (index: number | null) => {
    setSelectedTransportationRowIndex(index);
    setSelectedFeeType('both'); // Reset radio selection (will be overridden by payment calculation)
  };

  // Handle tuition statement PDF export
  const handleExportTuitionPDF = async () => {
    if (!student.sub_acct_code) return;
    
    try {
      setExportingTuition(true);
      await studentService.exportTuitionStatementPDF(student.sub_acct_code);
    } catch (error) {
      console.error('Error exporting tuition statement:', error);
      alert('Failed to export tuition statement PDF. Please try again.');
    } finally {
      setExportingTuition(false);
    }
  };

  // Handle transportation statement PDF export
  const handleExportTransportationPDF = async () => {
    if (!student.sub_acct_code) return;
    
    try {
      setExportingTransportation(true);
      await studentService.exportTransportationStatementPDF(student.sub_acct_code);
    } catch (error) {
      console.error('Error exporting transportation statement:', error);
      alert('Failed to export transportation statement PDF. Please try again.');
    } finally {
      setExportingTransportation(false);
    }
  };
  
  // Calculate payment amount based on selected fee type or selected rows
  useEffect(() => {
    // If any row is selected, calculate from selected rows
    if (selectedTuitionRowIndex !== null || selectedTransportationRowIndex !== null) {
      let tuitionAmount = 0;
      let transportationAmount = 0;

      if (selectedTuitionRowIndex !== null && tuitionStatement[selectedTuitionRowIndex]) {
        tuitionAmount = tuitionStatement[selectedTuitionRowIndex].running_balance;
      }

      if (selectedTransportationRowIndex !== null && transportationStatement[selectedTransportationRowIndex]) {
        transportationAmount = transportationStatement[selectedTransportationRowIndex].running_balance;
      }

      setPaymentAmount(tuitionAmount + transportationAmount);
      return;
    }

    // Otherwise, use fee type selection
    if (!apiFeeDetails) {
      setPaymentAmount(feeDetails.remainingAmount);
      return;
    }
    
    switch (selectedFeeType) {
      case 'tuition':
        setPaymentAmount(apiFeeDetails.tuition_fee_due);
        break;
      case 'transportation':
        setPaymentAmount(apiFeeDetails.transportation_fee_due);
        break;
      case 'both':
        setPaymentAmount(apiFeeDetails.total_fee_due);
        break;
      default:
        setPaymentAmount(0);
    }
  }, [selectedFeeType, apiFeeDetails, feeDetails.remainingAmount, selectedTuitionRowIndex, selectedTransportationRowIndex, tuitionStatement, transportationStatement]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 md:pb-40">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white p-6 shadow-lg">
        <div className="w-full px-4 md:max-w-7xl md:mx-auto">
          <h1 className="text-3xl font-bold">Student Fee Details</h1>
          <p className="text-primary-100">Review your student's fee information</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-2 py-8 mb-8">
        <div className="grid lg:grid-cols-[minmax(0,40%)_minmax(0,60%)] gap-6">{/* Left Column - Student Info & Fee Details */}
          <div className="space-y-6">
            {/* Student Information Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-4 border-b-2 border-gray-200">
                Student Information
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-1">
                    Student Name
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {feeDetails.studentName}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-1">
                    Student Code
                  </p>
                  <p className="text-2xl font-bold text-primary-600">
                    {feeDetails.studentCode}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-1">
                    Father's Name
                  </p>
                  <p className="text-xl text-gray-800">
                    {feeDetails.fatherName}
                  </p>
                </div>

                <div>
                  <p className="text-gray-500 text-sm uppercase tracking-wide mb-1">
                    Standard
                  </p>
                  <p className="text-xl text-gray-800">
                    Class {feeDetails.standard}
                  </p>
                </div>
              </div>
            </div>

            {/* Fee Information Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 pb-4 border-b-2 border-gray-200">
                Fee Details
              </h2>

          {apiFeeDetails && (apiFeeDetails.tuition_fee_due > 0 || apiFeeDetails.transportation_fee_due > 0) ? (
            <>
              {/* Fee Type Selection */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Select Fee Type to Pay</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {apiFeeDetails.tuition_fee_due > 0 && (
                    <button
                      onClick={() => {
                        setSelectedFeeType('tuition');
                        setSelectedTuitionRowIndex(null);
                        setSelectedTransportationRowIndex(null);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedFeeType === 'tuition' && selectedTuitionRowIndex === null && selectedTransportationRowIndex === null
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-300 hover:border-primary-400'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <input
                          type="radio"
                          checked={selectedFeeType === 'tuition' && selectedTuitionRowIndex === null && selectedTransportationRowIndex === null}
                          onChange={() => {}}
                          className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-600"
                        />
                        <span className="font-semibold text-gray-800 text-sm break-words">Tuition Fee Only</span>
                      </div>
                      <p className="text-xl font-bold text-primary-600 truncate" title={formatCurrency(apiFeeDetails.tuition_fee_due)}>
                        {formatCurrency(apiFeeDetails.tuition_fee_due)}
                      </p>
                    </button>
                  )}
                  
                  {apiFeeDetails.transportation_fee_due > 0 && (
                    <button
                      onClick={() => {
                        setSelectedFeeType('transportation');
                        setSelectedTuitionRowIndex(null);
                        setSelectedTransportationRowIndex(null);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedFeeType === 'transportation' && selectedTuitionRowIndex === null && selectedTransportationRowIndex === null
                          ? 'border-secondary-600 bg-secondary-50'
                          : 'border-gray-300 hover:border-secondary-400'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <input
                          type="radio"
                          checked={selectedFeeType === 'transportation' && selectedTuitionRowIndex === null && selectedTransportationRowIndex === null}
                          onChange={() => {}}
                          className="w-4 h-4 mt-0.5 flex-shrink-0 text-secondary-600"
                        />
                        <span className="font-semibold text-gray-800 text-sm break-words">Transportation Fee Only</span>
                      </div>
                      <p className="text-xl font-bold text-secondary-600 truncate" title={formatCurrency(apiFeeDetails.transportation_fee_due)}>
                        {formatCurrency(apiFeeDetails.transportation_fee_due)}
                      </p>
                    </button>
                  )}
                  
                  {apiFeeDetails.tuition_fee_due > 0 && apiFeeDetails.transportation_fee_due > 0 && (
                    <button
                      onClick={() => {
                        setSelectedFeeType('both');
                        setSelectedTuitionRowIndex(null);
                        setSelectedTransportationRowIndex(null);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        selectedFeeType === 'both' && selectedTuitionRowIndex === null && selectedTransportationRowIndex === null
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-300 hover:border-green-400'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <input
                          type="radio"
                          checked={selectedFeeType === 'both' && selectedTuitionRowIndex === null && selectedTransportationRowIndex === null}
                          onChange={() => {}}
                          className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600"
                        />
                        <span className="font-semibold text-gray-800 text-sm break-words">Both Fees</span>
                      </div>
                      <p className="text-xl font-bold text-green-600 truncate" title={formatCurrency(apiFeeDetails.total_fee_due)}>
                        {formatCurrency(apiFeeDetails.total_fee_due)}
                      </p>
                    </button>
                  )}
                </div>
              </div>

              {/* Selected Amount to Pay */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl mb-6 border-2 border-purple-200">
                <p className="text-gray-600 text-sm uppercase tracking-wide mb-2">
                  Amount to Pay
                </p>
                {selectedTuitionRowIndex !== null || selectedTransportationRowIndex !== null ? (
                  <>
                    <div className="space-y-2 mb-3">
                      {selectedTuitionRowIndex !== null && tuitionStatement[selectedTuitionRowIndex] && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Tuition Fee (Selected):</span>
                          <span className="text-xl font-bold text-blue-600">
                            {formatCurrency(tuitionStatement[selectedTuitionRowIndex].running_balance)}
                          </span>
                        </div>
                      )}
                      {selectedTransportationRowIndex !== null && transportationStatement[selectedTransportationRowIndex] && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Transportation Fee (Selected):</span>
                          <span className="text-xl font-bold text-green-600">
                            {formatCurrency(transportationStatement[selectedTransportationRowIndex].running_balance)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="border-t-2 border-purple-300 pt-3">
                      <p className="text-4xl font-bold text-purple-600">
                        {formatCurrency(paymentAmount)}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        Selected from Statement Rows
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-4xl font-bold text-purple-600">
                      {formatCurrency(paymentAmount)}
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedFeeType === 'tuition' && 'Tuition Fee'}
                      {selectedFeeType === 'transportation' && 'Transportation Fee'}
                      {selectedFeeType === 'both' && 'Tuition + Transportation Fees'}
                    </p>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div className="bg-blue-50 p-6 rounded-xl">
                <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">
                  Total Fee Amount
                </p>
                <p className="text-3xl font-bold text-blue-600">
                  {formatCurrency(feeDetails.feeAmount)}
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-xl">
                <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">
                  Amount Paid
                </p>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(feeDetails.paidAmount)}
                </p>
              </div>
            </div>
          )}

          {/* Remaining Amount - Only show if no API fee details */}
          {!apiFeeDetails && (
            <div
              className={`p-6 rounded-xl mb-6 ${
                feeDetails.remainingAmount > 0
                  ? 'bg-red-50'
                  : 'bg-green-50'
              }`}
            >
              <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">
                {feeDetails.remainingAmount > 0
                  ? 'Amount Due'
                  : 'Payment Status'}
              </p>
              <div className="flex items-center justify-between">
                <p
                  className={`text-3xl font-bold ${
                    feeDetails.remainingAmount > 0
                      ? 'text-red-600'
                      : 'text-green-600'
                  }`}
                >
                  {formatCurrency(feeDetails.remainingAmount)}
                </p>
                {feeDetails.remainingAmount === 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-200 rounded-full">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="font-semibold text-green-700">Paid</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Due Date */}
          <div className="bg-gray-50 p-6 rounded-xl">
            <p className="text-gray-500 text-sm uppercase tracking-wide mb-2">
              Due Date
            </p>
            <p className="text-xl font-semibold text-gray-800 mb-2">
              {formatDate(feeDetails.dueDate)}
            </p>
            {feeDetails.remainingAmount > 0 && (
              <p
                className={`text-sm font-semibold ${
                  isOverdue
                    ? 'text-red-600'
                    : daysUntilDue <= 7
                    ? 'text-orange-600'
                    : 'text-green-600'
                }`}
              >
                {isOverdue
                  ? `⚠️ Overdue by ${Math.abs(daysUntilDue)} days`
                  : daysUntilDue <= 7
                  ? `⏰ Due in ${daysUntilDue} days`
                  : `✓ Due in ${daysUntilDue} days`}
              </p>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {feeDetails.remainingAmount > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Payment Progress
            </h3>
            <div className="mb-4">
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-green-400 to-green-600 h-full rounded-full transition-all duration-300"
                  style={{
                    width: `${(feeDetails.paidAmount / feeDetails.feeAmount) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              {Math.round(
                (feeDetails.paidAmount / feeDetails.feeAmount) * 100
              )}% of fee paid
            </p>
          </div>
        )}
      </div>

      {/* Right Column - Fee Statements */}
      <div className="space-y-6">
        {/* Fee Statements Section */}
        {student.sub_acct_code && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Fee Statements
            </h2>
            
            {loadingStatements ? (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="mt-4 text-gray-600">Loading fee statements...</p>
              </div>
            ) : (
              <>
                {/* Info Box */}
                {(tuitionStatement.length > 0 || transportationStatement.length > 0) && (
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 rounded">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          <strong>Tip:</strong> You can select individual rows from the statements below to pay specific amounts. 
                          Only rows after the last payment with outstanding balance are selectable.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tuition Fee Statement */}
                <FeeStatementTable
                  title="Tuition Fees Statement"
                  data={tuitionStatement}
                  colorScheme="blue"
                  selectedRowIndex={selectedTuitionRowIndex !== null ? selectedTuitionRowIndex : undefined}
                  onRowSelect={handleTuitionRowSelect}
                  onExport={handleExportTuitionPDF}
                  isExporting={exportingTuition}
                />

                {/* Transportation Fee Statement */}
                <FeeStatementTable
                  title="Transportation Fees Statement"
                  data={transportationStatement}
                  colorScheme="green"
                  selectedRowIndex={selectedTransportationRowIndex !== null ? selectedTransportationRowIndex : undefined}
                  onRowSelect={handleTransportationRowSelect}
                  onExport={handleExportTransportationPDF}
                  isExporting={exportingTransportation}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  </div>

  {/* Fixed Bottom Action Bar (Desktop only, floating on mobile) */}
  <div className="md:fixed md:bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-50 mt-8 md:mt-0">
    <div className="max-w-7xl mx-auto px-2 py-4">
      {/* Fee Breakdown */}
      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Tuition Fee - Show if row selected OR (no rows selected AND fee type includes tuition) */}
        {((selectedTuitionRowIndex !== null && tuitionStatement[selectedTuitionRowIndex]) || 
          (selectedTuitionRowIndex === null && selectedTransportationRowIndex === null && 
           (selectedFeeType === 'tuition' || selectedFeeType === 'both'))) && (
          <div className="bg-blue-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">Tuition Fee</p>
            <p className="text-lg font-bold text-blue-600">
              {selectedTuitionRowIndex !== null && tuitionStatement[selectedTuitionRowIndex]
                ? formatCurrency(tuitionStatement[selectedTuitionRowIndex].running_balance)
                : apiFeeDetails
                ? formatCurrency(apiFeeDetails.tuition_fee_due)
                : formatCurrency(0)}
            </p>
            {selectedTuitionRowIndex !== null && (
              <p className="text-xs text-blue-600 mt-1">Selected from statement</p>
            )}
          </div>
        )}

        {/* Transportation Fee - Show if row selected OR (no rows selected AND fee type includes transportation) */}
        {((selectedTransportationRowIndex !== null && transportationStatement[selectedTransportationRowIndex]) || 
          (selectedTransportationRowIndex === null && selectedTuitionRowIndex === null && 
           (selectedFeeType === 'transportation' || selectedFeeType === 'both'))) && (
          <div className="bg-green-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600 mb-1">Transportation Fee</p>
            <p className="text-lg font-bold text-green-600">
              {selectedTransportationRowIndex !== null && transportationStatement[selectedTransportationRowIndex]
                ? formatCurrency(transportationStatement[selectedTransportationRowIndex].running_balance)
                : apiFeeDetails
                ? formatCurrency(apiFeeDetails.transportation_fee_due)
                : formatCurrency(0)}
            </p>
            {selectedTransportationRowIndex !== null && (
              <p className="text-xs text-green-600 mt-1">Selected from statement</p>
            )}
          </div>
        )}

        {/* Total - Always show */}
        <div className="bg-purple-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600 mb-1">Total Amount</p>
          <p className="text-xl font-bold text-purple-600">
            {formatCurrency(paymentAmount || feeDetails.remainingAmount)}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-4 px-6 rounded-lg transition-colors duration-200 text-lg"
        >
          Back
        </button>
        {(paymentAmount > 0 || feeDetails.remainingAmount > 0) ? (
          <button
            onClick={onProceedPayment}
            className="flex-1 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white font-bold py-4 px-6 rounded-lg transition-all duration-200 text-lg shadow-lg"
          >
            Proceed to Payment • {formatCurrency(paymentAmount || feeDetails.remainingAmount)}
          </button>
        ) : (
          <button
            disabled
            className="flex-1 bg-gray-400 text-gray-600 font-bold py-4 px-6 rounded-lg text-lg cursor-not-allowed"
          >
            Payment Completed
          </button>
        )}
      </div>
    </div>
  </div>
</div>
  );
};

export default StudentDetailPage;
